import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Flame, CalendarCheck, Check } from 'lucide-react';

interface FoodSectionProps {
  onOpenOrderApp: () => void;
}

export const FoodSection: React.FC<FoodSectionProps> = ({ onOpenOrderApp }) => {
  const { lang } = useLanguage();

  const text = {
    eyebrow: {
      fi: 'PUU-UUNIKEITTIÖ / THE HEARTH',
      en: 'WOOD-FIRED KITCHEN / THE HEARTH',
      sv: 'VEDUGNSKÖK / THE HEARTH'
    },
    title: {
      fi: 'Aito kiviuunin maku 48 tunnin hapanjuurella',
      en: 'Real Wood-Fired Taste on 48h Sourdough',
      sv: 'Äkta Vedugnssmak med 48h Surdeg'
    },
    desc: {
      fi: 'Jokainen pizzamme saa alkunsa huolella vaalatusta hapanjuuresta, joka fermentoituu vähintään kaksi vuorokautta. 450-asteinen kiviuunimme tekee pohjasta ilmavan ja rapean, samalla kun Lapin savuporo, metsäsienet ja italialainen puhvelinmozzarella saavat täydellisen paahteen.',
      en: 'Every pizza starts with slow-fermented wild sourdough nurtured over 48 hours. Our 450°C stone oven creates an airy, crisp blistered crust, beautifully complementing smoked Lapland reindeer, wild forest porcini, and sweet San Marzano tomatoes.',
      sv: 'Våra pizzor bakas på surdeg som fått jäsa i 48 timmar. Den 450 grader varma stenugnen ger en krispig och luftig botten med fyllningar som rökt ren, vildsvamp och äkta buffelmozzarella.'
    },
    items: [
      {
        name: { fi: 'Pohjoinen Poro & Tryffeli', en: 'Nordic Reindeer & Truffle Pizza', sv: 'Nordisk Ren & Tryffelpizza' },
        desc: {
          fi: 'Savustettua Lapin poroa, villitatti-tryffelikreemiä, Fior di Latte -mozzarellaa ja tuoretta puolukkaa.',
          en: 'Smoked Lapland reindeer, wild porcini truffle cream, Fior di Latte mozzarella and fresh lingonberries.',
          sv: 'Rökt ren från Lappland, Karljohan-tryffelkräm och färska lingon.'
        },
        tags: ['Suosikki', 'Laktoositon'],
        price: '21.50 €'
      },
      {
        name: { fi: 'Margherita San Marzano D.O.P', en: 'Margherita San Marzano D.O.P', sv: 'Margherita San Marzano D.O.P' },
        desc: {
          fi: 'Makeaa San Marzano -tomaattia, aitoa puhvelinmozzarellaa, tuoretta basilikaa ja neitsytoliiviöljyä.',
          en: 'Sweet San Marzano tomato sauce, authentic Buffalo mozzarella, fresh basil, and extra virgin olive oil.',
          sv: 'San Marzano-tomatsås, äkta buffelmozzarella och färsk basilika.'
        },
        tags: ['Kasvis', 'Klassikko'],
        price: '15.90 €'
      },
      {
        name: { fi: 'Kermainen Lohikeitto (Gluteeniton)', en: 'Creamy Finnish Salmon Soup', sv: 'Krämig Finsk Laxsoppa' },
        desc: {
          fi: 'Tuoretta merilohta, voissa haudutettua perunaa, runsaasti tuoretta tilliä ja saaristolaisleipää.',
          en: 'Fresh Finnish salmon, butter-braised new potatoes, abundant fresh dill, and archipelago bread.',
          sv: 'Färsk finsk lax, smörbräserad potatis, rikligt med dill och skärgårdsbröd.'
        },
        tags: ['Gluteeniton', 'Laktoositon'],
        price: '16.50 €'
      },
      {
        name: { fi: 'Gluteeniton Metsätattirisotto', en: 'Gluten-Free Porcini Risotto', sv: 'Glutenfri Karljohanrisotto' },
        desc: {
          fi: 'Kermainen Carnaroli-risotto, paistettuja herkkutatteja, kypsytettyä parmesaania ja valkotryffeliöljyä.',
          en: 'Creamy Carnaroli arborio risotto, sautéed wild porcini mushrooms, aged parmesan and white truffle oil.',
          sv: 'Krämig Carnarolirisotto med Karljohansvamp och vit tryffelolja.'
        },
        tags: ['Gluteeniton', 'Kasvis'],
        price: '19.90 €'
      }
    ],
    btn: {
      fi: 'Varaa Pöytä & Tilaa Ruoat',
      en: 'Book Your Table to Dine',
      sv: 'Boka Bord för Middag'
    }
  };

  return (
    <section id="food" className="os-page-section alt-bg">
      <div className="os-container">
        <div className="os-feature-split">
          {/* Visual Column */}
          <div className="os-feature-media">
            <img
              src="/images/artisan-pizza-flame.jpg"
              alt="Artisan wood-fired pizza with blistered sourdough crust at The Oven Story"
            />
          </div>

          {/* Story & Menu Column */}
          <div>
            <div className="os-section-header" style={{ marginBottom: '28px' }}>
              <span className="os-eyebrow">{text.eyebrow[lang]}</span>
              <h2 className="os-section-title">{text.title[lang]}</h2>
              <p className="os-section-desc">{text.desc[lang]}</p>
            </div>

            {/* Menu List */}
            <div className="os-menu-cards-list">
              {text.items.map((item, idx) => (
                <div key={idx} className="os-menu-card">
                  <div style={{ flex: 1 }}>
                    <h3 className="os-dish-name">{item.name[lang]}</h3>
                    <p className="os-dish-desc">{item.desc[lang]}</p>
                    <div className="os-dish-tags">
                      {item.tags.map((t, tIdx) => (
                        <span key={tIdx} className="os-pill-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="os-dish-price">{item.price}</div>
                </div>
              ))}
            </div>

            {/* Clear Section Action */}
            <div style={{ marginTop: '28px' }}>
              <button onClick={onOpenOrderApp} className="os-btn-book-table" style={{ padding: '14px 28px', fontSize: '1rem' }}>
                <CalendarCheck size={18} />
                <span>{text.btn[lang]}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
